import { alpha } from "@mui/material/styles";
import type { ThemeOptions } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";

import { buildAdminThemeComponents } from "./adminThemeComponents";

const foundation = {
    white: "#FFFFFF",
    purple: "#0C0140",
    purple80: "#3E307C",
    grey: "#787585",
    grey50: "#D9D9D9",
    greyText: "#6B7280",
    greyTextDark: "#72707D",
    pink: "#EF326C",
    pinkLight: "#ff639280",
    fail: "#DB3131",
    shadowBeige: "rgba(213, 201, 152, 0.2)",
} as const;

const backgroundTokens = {
    beige: "#FFFCF5",
    peach: "#FDF6ED",
    merino: "#F5EEE2",
    lavender: "#EDEAFA",
} as const;

const graphTokens = {
    green: "#0FB35B",
    teal: "#3CC7BF",
    red: "#F25252",
    orange: "#FBA95F",
    yellow: "#FFD600",
    blue: "#0056FD",
    deepBlue: "#1850a3",
    fushia: "#DB3DCE",
    pink: foundation.pink,
    pinkLight: foundation.pinkLight,
    purple80: foundation.purple80,
} as const;

const breakpoints = {
    values: {
        xs: 0,
        sm: 600,
        md: 768,
        lg: 1200,
        xl: 1600,
    },
};

const typography = {
    fontFamily: "Poppins, sans-serif",
    h1: {
        fontSize: "2rem",
        fontWeight: 600,
        lineHeight: 1.4375,
        color: foundation.purple,
    },
    h2: {
        fontSize: "1.5rem",
        fontWeight: 400,
        lineHeight: 1.25,
        color: foundation.purple,
    },
    h3: {
        fontSize: "1.125rem",
        fontWeight: 400,
        lineHeight: 1.333,
    },
    h4: {
        fontSize: "1.25rem",
        fontWeight: 600,
        lineHeight: 1.3,
    },
    h5: {
        fontSize: "1.125rem",
        fontWeight: 600,
        lineHeight: 1.5,
    },
    h6: {
        fontSize: "1rem",
        fontWeight: 600,
        lineHeight: 1.5,
    },
    subtitle1: {
        fontSize: "1rem",
        fontWeight: 500,
        lineHeight: 1.28,
    },
    subtitle2: {
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: 1.57,
    },
    body1: {
        fontSize: "1rem",
        fontWeight: 400,
        lineHeight: 1.25,
    },
    body2: {
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: 1.286,
    },
    caption: {
        fontSize: "0.75rem",
        fontWeight: 400,
        lineHeight: 1.33,
        color: foundation.grey,
    },
    overline: {
        fontSize: "0.75rem",
        fontWeight: 600,
        lineHeight: 1,
        letterSpacing: "0.08em",
        textTransform: "uppercase" as const,
    },
};

const spacing = 4;

export const buildAdminThemeOptions = (
    mode: "light" | "dark",
): ThemeOptions => {
    const isLight = mode === "light";

    const palette = {
        mode,
        primary: {
            main: isLight ? foundation.pink : "#ffe0e9",
            contrastText: foundation.white,
        },
        secondary: {
            main: foundation.purple,
            contrastText: foundation.white,
        },
        error: {
            main: foundation.fail,
            contrastText: foundation.white,
        },
        success: {
            main: graphTokens.green,
            contrastText: foundation.white,
        },
        warning: {
            main: graphTokens.orange,
            contrastText: foundation.purple,
        },
        info: {
            main: graphTokens.blue,
            contrastText: foundation.white,
        },
        divider: isLight ? foundation.grey50 : alpha(foundation.white, 0.12),
        background: {
            default: isLight ? backgroundTokens.beige : "#121212",
            paper: isLight ? foundation.white : "#1e1e1e",
        },
        text: {
            primary: isLight ? foundation.purple : foundation.white,
            secondary: isLight
                ? foundation.greyText
                : alpha(foundation.white, 0.72),
        },
    } as const;

    return {
        palette,
        breakpoints,
        typography,
        spacing,
        shape: {
            borderRadius: 12,
        },
        components: buildAdminThemeComponents({
            isLight,
            foundation,
            backgroundTokens,
            palette,
            typographyFontFamily: typography.fontFamily,
        }) as NonNullable<ThemeOptions["components"]>,
        custom: {
            sidebar: {
                width: 280,
                collapsedWidth: 64,
            },
            header: {
                height: 64,
            },
            layout: {
                contentPadding: spacing * 6,
                sectionSpacing: spacing * 4,
            },
        },
    };
};
